import * as React from 'react'
import { isRouteErrorResponse, useRouteError } from 'react-router'

import { cn } from '@modules/lib/lib'
import { Button } from '@modules/shadcn/ui/Button/Button'

interface Props {
  error: unknown
  /** 表示するパス。省略時は window.location.pathname を使用 */
  pathname?: string
  /**
   * ルート単位のErrorBoundaryで、サイドバーの中に収めて描くかどうか。
   * trueにすると全画面高さ（`min-h-screen`）をやめ、その領域だけを占める。
   */
  inline?: boolean
}

/**
 * ErrorBoundary用のエラー画面。
 * 404（存在しないルート）とそれ以外のエラーで表示を出し分ける。
 */
export const RouteErrorScreen: React.FC<Props> = ({ error, pathname, inline = false }) => {
  const isNotFound = isRouteErrorResponse(error) && error.status === 404
  const currentPath = pathname ?? (typeof window !== 'undefined' ? window.location.pathname : '')
  const errorMessage = error instanceof Error ? error.message : ''

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center gap-4 px-4',
        inline ? 'py-24' : 'min-h-screen',
      )}
    >
      {isNotFound ? (
        <>
          <h1 className="text-xl font-semibold">ページが見つかりません</h1>
          {currentPath && (
            <p className="text-sm text-muted-foreground font-mono break-all">{currentPath}</p>
          )}
        </>
      ) : (
        <>
          <h1 className="text-xl font-semibold">エラーが発生しました</h1>
          {/*
            内部例外のメッセージ（例: idCheckが投げるTypeErrorの英文）を利用者にそのまま見せない。
            開発時（import.meta.env.DEV）だけ、切り分け用に生メッセージを小さく添える。
          */}
          <p className="text-sm text-muted-foreground">
            ページを表示できませんでした。URLが正しいか確認してください。
          </p>
          {import.meta.env.DEV && errorMessage && (
            <p className="text-xs text-muted-foreground/70 break-all">{errorMessage}</p>
          )}
        </>
      )}
      <div className="flex gap-3">
        {/* ErrorBoundary 内は router コンテキストが不安定なため Link ではなく <a> を使う */}
        <Button asChild variant="outline">
          <a href="/">トップへ戻る</a>
        </Button>
        <Button onClick={() => window.location.reload()}>再読み込み</Button>
      </div>
    </div>
  )
}

/**
 * ルート単位の `ErrorBoundary`。各ルートファイルで `export const ErrorBoundary = RouteErrorBoundary`
 * と再エクスポートして使う。
 *
 * 置く理由: ルートパラメータの検査（`idCheck`）はレンダー中に `TypeError` を投げ、
 * 不正なslugも同様に描画時のエラーになる。ルート単位の境界が無いとroot ErrorBoundaryまで
 * 上がってしまい、サイドバーごと画面全体がエラー画面に置き換わる
 * （state-lifecycle-audit.md §4.2）。境界をルートに置くと、サイドバー・ヘッダは残り、
 * 中身の領域だけがエラー表示になる。
 */
export const RouteErrorBoundary: React.FC = () => {
  const error = useRouteError()
  console.error(error)
  return <RouteErrorScreen error={error} inline />
}
